import _ from 'lodash';
import { IconNames } from '@blueprintjs/icons';
import { Intent } from '@blueprintjs/core';
import { appListStore } from '../../../stores/AppListStore';
import { directoryStore } from '../../../stores/DirectoryStore';
import { SegmentAnalytics } from '../../../lib/SegmentAnalytics';
import {
  AppFeature,
  AppWorksWith,
  OPAL_CATEGORY,
} from '../../../types/AppConstants';
import {
  FilterMapper,
  isWorksWithFilter,
  isFeaturesFilter,
} from '../../../utils/FeatureMapper';
import { VendorType, VendorMapper } from '../../../utils/VendorMapper';
import { FilterConfig } from './types';

/**
 * Helper function to check if any apps have a specific feature.
 */
export function hasAppsWithFeature(feature: AppFeature): boolean {
  return (
    !directoryStore.isODPAccount() &&
    appListStore.allApps.some(
      (app) => app.features && app.features.includes(feature)
    )
  );
}

/**
 * Helper function to check if any apps have a specific feature.
 * For CMS feature, always returns true as all apps are compatible for now.
 */
function hasAppsWithWorksWith(filter: AppWorksWith): boolean {
  return (
    !directoryStore.isODPAccount() &&
    (filter === AppWorksWith.OPAL
      ? appListStore.allApps.some((app) =>
          app.metadata.categories.some((category) => category === filter)
        )
      : appListStore.allApps.filter(
          (app) =>
            app.features &&
            app.features.some((f) => f !== AppFeature.OPAL_TOOL_APP)
        ).length > 0)
  );
}

/**
 * Helper function to check if any apps are from a specific vendor type.
 */
function hasAppsWithVendor(vendorType: VendorType): boolean {
  return appListStore.allApps.some(
    (app) => VendorMapper.normalize(app.metadata.vendor) === vendorType
  );
}

/**
 * Click handler for category filter.
 * Toggles the category filter on/off.
 * Special case: handles "installed" as a pseudo-category.
 */
function onCategoryClick(category: string | undefined) {
  if (!category || appListStore.filter.category === category) {
    appListStore.clearFilters();
    return;
  }
  appListStore.setFilter({ category });
  SegmentAnalytics.track('Filter category', { page: category });
}

/**
 * Click handler for feature filter (Works With and Features).
 * Handles the special case where CMS shows all apps.
 */
function onFeatureClick(feature: AppFeature | undefined) {
  if (!feature || appListStore.filter.feature === feature) {
    appListStore.clearFilters();
    return;
  }

  appListStore.setFilter({ feature });
  SegmentAnalytics.track('Filter feature', { feature });
}

/**
 * Click handler for Built By filter.
 * Toggles the vendor filter on/off.
 */
function onBuiltByClick(builtBy: VendorType | undefined) {
  if (!builtBy || appListStore.filter.builtBy === builtBy) {
    appListStore.clearFilters();
    return;
  }

  appListStore.setFilter({ builtBy });
  SegmentAnalytics.track('Filter built by', { builtBy });
}

/**
 * Click handler for installed filter.
 * Toggles between installed apps and all apps.
 */
function onInstalledClick() {
  if (appListStore.filter.installed) {
    appListStore.clearFilters();
    return;
  } else {
    appListStore.setFilter({ installed: true });
    SegmentAnalytics.track('Filter category', { page: 'Installed' });
  }
}

/**
 * Click handler for installed filter.
 * Toggles between installed apps and all apps.
 */
function onWorksWithClick(worksWith: AppWorksWith | undefined) {
  if (appListStore.filter.worksWith === worksWith) {
    appListStore.clearFilters();
    return;
  } else {
    appListStore.setFilter({ worksWith });
    SegmentAnalytics.track('Filter works with', { worksWith });
  }
}

/**
 * Root filter configuration.
 */
export const rootFilterConfig: FilterConfig<'installed' | 'all'> = {
  id: 'rootFilter',
  title: '',
  getOptions: () => {
    const items: any = [
      {
        label: 'All',
        value: 'all',
        hasDivider: true,
      },
    ];
    if (directoryStore.trackerId) {
      items.unshift({
        label: 'Installed',
        value: 'installed',
        icon: IconNames.TICK_CIRCLE,
        intent: Intent.SUCCESS,
      });
    }
    return items;
  },
  getSelectedValue: () =>
    !!appListStore.filter.installed
      ? 'installed'
      : _.isEmpty(appListStore.filter)
      ? 'all'
      : undefined,
  onSelect: (value: 'installed' | 'all' | undefined) =>
    value === 'installed' ? onInstalledClick() : appListStore.clearFilters(),
};

/**
 * Category filter configuration.
 * Shows app categories with "Installed" special option and "All" option.
 */
export const categoryFilterConfig: FilterConfig<string> = {
  id: 'category',
  title: 'Categories',
  getOptions: () =>
    appListStore.categories
      // skip Opal category from category list, this'll be part of WorksWith
      .filter((category) => category !== OPAL_CATEGORY)
      .map((category) => ({
        label: category,
        value: category,
      })),
  getSelectedValue: () => appListStore.filter.category,
  onSelect: onCategoryClick,
};

/**
 * Features filter configuration.
 * Shows feature options, with Opal shown separately if available.
 * Only shows options if there are apps with that feature.
 */
export const featuresFilterConfig: FilterConfig<AppFeature> = {
  id: 'featuresFilter',
  title: 'Features',
  getOptions: () =>
    FilterMapper.getAllFilters()
      .filter(isFeaturesFilter)
      .filter(hasAppsWithFeature)
      .map((filter) => ({
        label: FilterMapper.toUILabel(filter),
        value: filter,
      })),
  getSelectedValue: () => appListStore.filter.feature,
  onSelect: onFeatureClick,
};

/**
 * Works With (Feature) filter configuration.
 * Shows feature options, with Opal shown separately if available.
 * Only shows options if there are apps with that feature.
 */
export const worksWithFilterConfig: FilterConfig<AppWorksWith> = {
  id: 'worksWithintent',
  title: 'Works With',
  getOptions: () =>
    FilterMapper.getAllFilters()
      .filter(isWorksWithFilter)
      .filter(hasAppsWithWorksWith)
      .map((filter) => ({
        label: FilterMapper.toUILabel(filter),
        value: filter,
      })),
  getSelectedValue: () => appListStore.filter.worksWith,
  onSelect: onWorksWithClick,
};

/**
 * Built By (Vendor) filter configuration.
 * Shows Optimizely vs Partner filter.
 * Only shows options if there are apps from that vendor.
 */
export const builtByFilterConfig: FilterConfig<VendorType> = {
  id: 'builtBy',
  title: 'Built By',
  getOptions: () =>
    (['Optimizely', 'Partner'] as VendorType[])
      .filter(hasAppsWithVendor)
      .map((vendorType) => ({
        label: vendorType,
        value: vendorType,
      })),
  getSelectedValue: () => appListStore.filter.builtBy,
  onSelect: onBuiltByClick,
};

export const ALL_FILTER_CONFIGS = {
  root: rootFilterConfig,
  category: categoryFilterConfig,
  worksWith: worksWithFilterConfig,
  features: featuresFilterConfig,
  builtBy: builtByFilterConfig,
};
